import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { 
  CheckCircle2, 
  Clock, 
  XCircle, 
  Mail, 
  MessageSquare, 
  Link as LinkIcon,
  TrendingUp,
  Users
} from 'lucide-react';

interface BacklinkStats {
  total: number;
  published: number;
  pending: number;
  invalid: number;
  emailSent: number;
  awaitingReply: number;
  connected: number;
  followUp1: number;
  followUp2: number;
}

interface BacklinkDashboardProps {
  userId: string;
}

export const BacklinkDashboard = ({ userId }: BacklinkDashboardProps) => {
  const [stats, setStats] = useState<BacklinkStats>({
    total: 0,
    published: 0,
    pending: 0,
    invalid: 0,
    emailSent: 0,
    awaitingReply: 0,
    connected: 0,
    followUp1: 0,
    followUp2: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    
    // Subscribe to real-time updates
    const channel = supabase
      .channel('backlinks-stats')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'backlinks',
          filter: `agent_id=eq.${userId}`
        },
        () => {
          fetchStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const fetchStats = async () => {
    const { data, error } = await supabase
      .from('backlinks')
      .select('processing_status, outreach_status, reply_received')
      .eq('agent_id', userId);

    if (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
      return;
    }

    const newStats: BacklinkStats = {
      total: data.length,
      published: data.filter(b => b.processing_status === 'published').length,
      pending: data.filter(b => b.processing_status === 'pending').length,
      invalid: data.filter(b => b.processing_status === 'failed').length,
      emailSent: data.filter(b => ['email_sent', 'awaiting_reply', 'follow_up_1', 'follow_up_2'].includes(b.outreach_status)).length,
      awaitingReply: data.filter(b => b.outreach_status === 'awaiting_reply').length,
      connected: data.filter(b => b.reply_received === true).length,
      followUp1: data.filter(b => b.outreach_status === 'follow_up_1').length,
      followUp2: data.filter(b => b.outreach_status === 'follow_up_2').length,
    };

    setStats(newStats);
    setLoading(false);
  };

  const StatCard = ({ 
    title, 
    value, 
    icon: Icon, 
    color, 
    description 
  }: { 
    title: string; 
    value: number; 
    icon: any; 
    color: string; 
    description?: string;
  }) => (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${color}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Backlink Outreach Dashboard</CardTitle>
          <CardDescription>Loading statistics...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const successRate = stats.total > 0 
    ? Math.round((stats.connected / stats.emailSent) * 100) 
    : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Automated Backlink Outreach Dashboard
          </CardTitle>
          <CardDescription>
            Real-time monitoring of automated backlink creation, outreach, and follow-ups
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-primary/5 rounded-lg">
              <div className="text-3xl font-bold text-primary">{stats.total}</div>
              <div className="text-sm text-muted-foreground mt-1">Total Backlinks</div>
            </div>
            <div className="text-center p-4 bg-green-500/10 rounded-lg">
              <div className="text-3xl font-bold text-green-600">{stats.connected}</div>
              <div className="text-sm text-muted-foreground mt-1">Connected</div>
            </div>
            <div className="text-center p-4 bg-blue-500/10 rounded-lg">
              <div className="text-3xl font-bold text-blue-600">{stats.emailSent}</div>
              <div className="text-sm text-muted-foreground mt-1">Emails Sent</div>
            </div>
            <div className="text-center p-4 bg-orange-500/10 rounded-lg">
              <div className="text-3xl font-bold text-orange-600">{successRate}%</div>
              <div className="text-sm text-muted-foreground mt-1">Success Rate</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Published Backlinks"
          value={stats.published}
          icon={CheckCircle2}
          color="text-green-600"
          description="Active and validated"
        />
        
        <StatCard
          title="Outreach Sent"
          value={stats.emailSent}
          icon={Mail}
          color="text-blue-600"
          description="Automated emails delivered"
        />
        
        <StatCard
          title="Awaiting Reply"
          value={stats.awaitingReply}
          icon={Clock}
          color="text-orange-600"
          description="Follow-ups scheduled"
        />
        
        <StatCard
          title="Connected"
          value={stats.connected}
          icon={MessageSquare}
          color="text-green-600"
          description="Responses received"
        />
        
        <StatCard
          title="Invalid Links"
          value={stats.invalid}
          icon={XCircle}
          color="text-red-600"
          description="Domain unreachable"
        />
        
        <StatCard
          title="Follow-ups Active"
          value={stats.followUp1 + stats.followUp2}
          icon={Users}
          color="text-purple-600"
          description="Automated sequences"
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Status Legend</CardTitle>
          <CardDescription>Understanding the automated workflow</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex items-center gap-3">
              <Badge className="bg-blue-600">Published</Badge>
              <span className="text-sm text-muted-foreground">
                Backlink created and validated
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-orange-600">Pending Outreach</Badge>
              <span className="text-sm text-muted-foreground">
                Email available, ready to send
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-purple-600">Email Sent</Badge>
              <span className="text-sm text-muted-foreground">
                Initial outreach delivered
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-yellow-600">Awaiting Reply</Badge>
              <span className="text-sm text-muted-foreground">
                Follow-up scheduled
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Badge className="bg-green-600">Connected</Badge>
              <span className="text-sm text-muted-foreground">
                Response received
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="destructive">Invalid Link</Badge>
              <span className="text-sm text-muted-foreground">
                Domain unreachable
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};